import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Alert } from '@material-ui/lab';
import { AppBar, Dialog, DialogContent, IconButton, Tab, Tabs, Toolbar, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import {
  fetchSerialNumberConfiguration,
  fetchSerialNumberHistory,
  getSerialNumberConfiguration,
  getSerialNumberHistory,
  getSerialNumbersApiErrors,
  getSerialNumbersLoaders,
  resetSerialNumberHistoryAndConfiguration
} from './serialNumbers';
import { useSerialDownloads } from './useSerialDownloads';
import { SerialNumberHistoryTimeline } from './SerialNumberHistoryTimeline';
import { SerialNumberConfiguration } from './SerialNumberConfiguration';

interface SerialNumberSnapshotsProps {
  closeFn: () => void;
  open: boolean;
  product: {
    id: string;
    name: string;
  };
  serial: string;
}

export const SerialNumberSnapshots: FC<SerialNumberSnapshotsProps> = ({ closeFn, open, product, serial }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useSelector(getSerialNumberHistory);
  const configuration = useSelector(getSerialNumberConfiguration);
  const api = useSelector(getSerialNumbersLoaders);
  const errors = useSelector(getSerialNumbersApiErrors);
  const { downloadConfiguration, downloadHistory } = useSerialDownloads(product.id, product.name, serial);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (!open || serial == '' || product.id == '') {
      dispatch(resetSerialNumberHistoryAndConfiguration());
    } else if (selectedTab === 0) {
      dispatch(fetchSerialNumberHistory({ productId: product.id, serialNumber: serial }));
    } else {
      dispatch(fetchSerialNumberConfiguration({ productId: product.id, serialNumber: serial }));
    }
  }, [open, serial, product, selectedTab]);

  return (
    <Dialog aria-labelledby="simple-modal-title" aria-describedby="simple-modal-description" open={open} fullScreen>
      <AppBar position="sticky">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={closeFn} aria-label="close">
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" style={{ display: 'flex', flex: 1, justifyContent: 'center' }}>
            {product.name} {serial}
          </Typography>

          <IconButton
            edge="end"
            color="inherit"
            arial-label="download"
            disabled={api.isLoading}
            onClick={selectedTab === 0 ? downloadHistory : downloadConfiguration}
          >
            <GetAppIcon />
          </IconButton>
        </Toolbar>
        <Tabs value={selectedTab} onChange={(_, newIndex: number) => setSelectedTab(newIndex)}>
          <Tab label={t('history.timeline')} />
          <Tab label={t('history.current configuration')} />
        </Tabs>
      </AppBar>

      <DialogContent>
        {errors.loadingError ? (
          <Alert severity="error">{errors.loadingError}</Alert>
        ) : (
          <>
            <div hidden={selectedTab !== 0}>
              <SerialNumberHistoryTimeline history={history} isLoading={api.isLoading} />
            </div>
            <div hidden={selectedTab !== 1}>
              <SerialNumberConfiguration configuration={configuration} isLoading={api.isLoading} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
